import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatIDR } from "@/lib/format";
import { useLang } from "@/context/LanguageContext";

export default function CartSheet() {
  const { items, open, setOpen, updateQty, removeItem, subtotal } = useCart();
  const { t } = useLang();
  const navigate = useNavigate();

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="bg-white w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
          <SheetTitle className="font-display text-2xl tracking-tight">{t("cart.title")}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4" data-testid="cart-items">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16">
              <div className="w-16 h-16 rounded-full bg-zinc-100 flex items-center justify-center mb-4">
                <ShoppingBag className="w-7 h-7 text-zinc-400" />
              </div>
              <div className="font-semibold text-zinc-900">{t("cart.empty")}</div>
              <div className="text-sm text-zinc-500 mt-1">{t("cart.emptyHint")}</div>
              <Button data-testid="cart-empty-shop-btn" onClick={() => { setOpen(false); navigate("/shop"); }} className="mt-6 rounded-full bg-brand hover:bg-brand-hover text-white px-6">{t("cart.browseShop")}</Button>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li key={item.product_id} className="flex gap-4" data-testid={`cart-item-${item.product_id}`}>
                  <div className="w-20 h-20 rounded-xl overflow-hidden product-image-bg flex items-center justify-center flex-shrink-0">
                    <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm text-zinc-900 truncate">{item.name}</div>
                    <div className="text-sm text-zinc-500 mt-0.5">{formatIDR(item.price)}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <button data-testid={`cart-dec-${item.product_id}`} onClick={() => updateQty(item.product_id, item.quantity - 1)} className="w-7 h-7 rounded-full border border-zinc-200 hover:border-zinc-400 flex items-center justify-center">
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-sm font-semibold w-6 text-center">{item.quantity}</span>
                      <button data-testid={`cart-inc-${item.product_id}`} onClick={() => updateQty(item.product_id, item.quantity + 1)} className="w-7 h-7 rounded-full border border-zinc-200 hover:border-zinc-400 flex items-center justify-center">
                        <Plus className="w-3 h-3" />
                      </button>
                      <button data-testid={`cart-remove-${item.product_id}`} onClick={() => removeItem(item.product_id)} className="ml-auto text-zinc-400 hover:text-brand transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-zinc-900 whitespace-nowrap">{formatIDR(item.price * item.quantity)}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-zinc-100 px-6 py-5 space-y-3 bg-white">
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-500">{t("cart.subtotal")}</span>
              <span className="font-semibold text-zinc-900" data-testid="cart-subtotal">{formatIDR(subtotal)}</span>
            </div>
            <div className="text-xs text-zinc-500">{t("cart.shippingNotice")}</div>
            <Button data-testid="cart-checkout-btn" onClick={() => { setOpen(false); navigate("/checkout"); }} className="w-full rounded-full bg-brand hover:bg-brand-hover text-white py-6 text-base font-semibold">
              {t("cart.checkout")} — {formatIDR(subtotal)}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
